import React, { useState } from "react";

import { useFormContext } from "react-hook-form";
import { v4 as uuidV4 } from "uuid";

import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { JinjaBodyEditor } from "src/customConnectionNode/editorComponents/JinjaBodyEditor";
import { KeyValueArrayEditor } from "src/customConnectionNode/editorComponents/KeyValueArrayEditor";
import { RawBodyEditor } from "src/customConnectionNode/editorComponents/RawBodyEditor";
import {
  BodyOption,
  CustomConnectionForm,
  JinjaBodyContentType,
  KeyValuePair,
} from "src/customConnectionNode/types";
import type { SingleMappingBE } from "src/integrationNode/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { checkType } from "src/utils/typeUtils";

type Props = { immutable: boolean; runFieldErrors: FieldErrors | undefined };

type JinjaState = {
  template: string;
  source: SingleMappingBE;
  content_type: JinjaBodyContentType;
};

export const BodyEditor: React.FC<Props> = ({ immutable, runFieldErrors }) => {
  const formProps = useFormContext<CustomConnectionForm>();
  const isManifestBuilderEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.manifestBuilder,
  );

  // Using local state for body values so content is preserved when toggling
  // between options.
  const [keyValueState, setKeyValueState] = useState<KeyValuePair[]>([]);
  const [rawState, setRawState] = useState("");
  const [jinjaState, setJinjaState] = useState<JinjaState>({
    template: "",
    source: { id: uuidV4(), expression: "" },
    content_type: "json",
  });

  const currentSelected = formProps.getValues("body.value.selected");

  const saveCurrentState = () => {
    if (currentSelected === "key_values") {
      setKeyValueState(formProps.getValues("body.value.key_values"));
    } else if (currentSelected === "raw") {
      setRawState(formProps.getValues("body.value.raw"));
    } else if (currentSelected === "jinja_template") {
      setJinjaState({
        template: formProps.getValues("body.value.template"),
        source: formProps.getValues("body.value.source"),
        content_type: formProps.getValues("body.value.content_type"),
      });
    }
  };

  const onSelectedChanged = (selectedValue: BodyOption) => {
    saveCurrentState();

    if (selectedValue === "key_values") {
      formProps.setValue("body.value", {
        selected: "key_values",
        key_values: keyValueState,
      });
    }
    if (selectedValue === "raw") {
      formProps.setValue("body.value", {
        selected: "raw",
        raw: rawState,
      });
    }
    if (isManifestBuilderEnabled && selectedValue === "jinja_template") {
      formProps.setValue("body.value", {
        selected: "jinja_template",
        template: jinjaState.template,
        source: jinjaState.source,
        content_type: jinjaState.content_type,
      });
    }
  };

  return (
    <Card className="w-full">
      <SimpleRadioGroup
        className="mb-2"
        disabled={immutable}
        orientation="horizontal"
        value={currentSelected}
        onValueChange={(value) => onSelectedChanged(value as BodyOption)}
      >
        <SimpleRadioGroup.Item
          label="Key-value"
          labelClassName="pl-2 mr-4"
          value={checkType<BodyOption>("key_values")}
        />
        <SimpleRadioGroup.Item
          label="Raw"
          labelClassName="pl-2 mr-4"
          value={checkType<BodyOption>("raw")}
        />
        {isManifestBuilderEnabled && (
          <SimpleRadioGroup.Item
            label="Jinja template"
            labelClassName="pl-2"
            value={checkType<BodyOption>("jinja_template")}
          />
        )}
      </SimpleRadioGroup>
      {currentSelected === "key_values" && (
        <KeyValueArrayEditor
          addButtonText="Add key"
          fieldName="body.value.key_values"
          immutable={immutable}
          runFieldErrors={runFieldErrors}
        />
      )}
      {currentSelected === "raw" && (
        <RawBodyEditor immutable={immutable} runFieldErrors={runFieldErrors} />
      )}
      {isManifestBuilderEnabled && currentSelected === "jinja_template" && (
        <JinjaBodyEditor
          immutable={immutable}
          runFieldErrors={runFieldErrors}
        />
      )}
    </Card>
  );
};
